// Answer Handler for PracticeTest
// Extracted to reduce file size and prevent Cursor crashes

import { startTransition } from 'react';
import { successHaptic, errorHaptic } from '../../utils/haptics';
import { gamificationService } from '../../services/gamificationService';
import { adaptiveDifficultyService } from '../../services/adaptiveDifficultyService';
import { aiAdaptiveLearningService } from '../../services/aiAdaptiveLearningService';
import { incorrectAnswerTracker } from '../../services/incorrectAnswerTracker';
import { logger } from '../../utils/logger';
import { safeArrayAt } from '../../utils/safeRender';
import type { HandlersContext } from './PracticeTest.handlers.types';

// Handle answer selection
export const createHandleAnswer = (context: HandlersContext) => {
  return (answerId: string) => {
    const { state, dispatch, questions, testId, motivationWordRef, previousAnswerCorrectRef, calculateMotivationWordWrapper, isMuted, questionStartTime, recentPerformance } = context;

    if (state.isAnswered) {
      return;
    }
    if (state.answerInputDisabledUntil > 0 && Date.now() < state.answerInputDisabledUntil) {
      return;
    }

    const currentQ = safeArrayAt(questions, state.currentQuestionIndex);
    if (!currentQ) {
      logger.warn('handleAnswer: no question at index', state.currentQuestionIndex, { questionsLen: questions?.length });
      return;
    }

    // Update UI state IMMEDIATELY (single render via reducer)
    const isCorrect = answerId === currentQ.correctAnswerId;
    const calculatedMotivationWord = calculateMotivationWordWrapper(isCorrect);
    motivationWordRef.current = calculatedMotivationWord;
    
    // Store for proactive feedback
    previousAnswerCorrectRef.current = isCorrect;
    
    // Track incorrect answers for targeted practice (Focus behavior)
    if (!isCorrect && testId && testId !== 'mock-exam' && currentQ) {
      incorrectAnswerTracker.trackIncorrectAnswer(
        testId,
        currentQ.id || String(state.currentQuestionIndex),
        answerId,
        currentQ.correctAnswerId || ''
      );
    }
    
    // Single dispatch updates all related state atomically
    dispatch({ 
      type: 'SELECT_ANSWER', 
      payload: { answerId, isCorrect, motivationWord: calculatedMotivationWord } 
    });

    if (testId !== 'mock-exam') {
      context.runCoachAfterAnswerRef?.current?.({
        isCorrect,
        answerId,
        currentQ,
        muted: isMuted,
      });
    }
    
    // Defer ALL non-critical work to requestIdleCallback (Netflix pattern)
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        
        // Haptic feedback (non-blocking)
        if (!isMuted && currentQ) {
          if (answerId === currentQ.correctAnswerId) {
            successHaptic();
          } else {
            errorHaptic();
          }
        }
        
        // Award XP (non-blocking)
        if (currentQ && answerId === currentQ.correctAnswerId) {
          gamificationService.awardXP('correct_answer');
        }
      });
      
      // Do async work in background (non-blocking)
      const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
      
      if (testId && testId !== 'mock-exam' && currentQ) {
        if (!currentQ.text) {
          logger.error('Invalid question encountered:', currentQ);
        }
        
        // Calculate difficulty asynchronously to avoid blocking
        const questionDifficulty = (currentQ as any)?.difficulty || 'medium';
        // Defer difficulty calculation if needed (non-blocking)
        if (!questionDifficulty && currentQ?.text) {
          setTimeout(() => {
            const _calculated = adaptiveDifficultyService.calculateQuestionDifficulty(currentQ);
            // Store for future use if needed
          }, 0);
        }
        
        const newCorrect = recentPerformance.correct + (isCorrect ? 1 : 0);
        const newTotal = recentPerformance.total + 1;
        const newAverageTime = (recentPerformance.averageTime * recentPerformance.total + timeSpent) / newTotal;
        const updatedPerformance = {
          correct: newCorrect,
          total: newTotal,
          averageTime: newAverageTime
        };
        
        // Update performance state (non-blocking, deferred)
        startTransition(() => {
          dispatch({ type: 'SET_RECENT_PERFORMANCE', payload: updatedPerformance });
        });
        
        // Adaptive learning (fully async, non-blocking)
        aiAdaptiveLearningService.adjustDifficultyIntelligently(
          state.currentDifficultyLevel,
          isCorrect,
          timeSpent,
          questionDifficulty,
          updatedPerformance
        )
          .then(adjustment => {
            startTransition(() => {
              dispatch({ type: 'SET_DIFFICULTY_LEVEL', payload: adjustment.newLevel });
            });
            // Defer logging
            if ('requestIdleCallback' in window) {
              (window as any).requestIdleCallback(() => {
                logger.debug('AI Adjustment:', adjustment.reasoning);
              });
            }
          })
          .catch(() => {
            const newDifficulty = adaptiveDifficultyService.adjustDifficultyAfterAnswer(
              state.currentDifficultyLevel,
              isCorrect,
              timeSpent,
              questionDifficulty
            );
            startTransition(() => {
              dispatch({ type: 'SET_DIFFICULTY_LEVEL', payload: newDifficulty });
            });
          });
      }
    }
  };
};
